<template>
  <div class="alarm-item" :class="setClass(status)" ref="reference">
    <div class="alarm-scrollable">
      <div class="alarm-content">
        <div class="alarm-field">
          <small class="d-block text-muted">Sensor</small>
          <select class="form-control text-center" v-model="selectedSensor" @change="changeSelectedSensor">
            <option :value="null" disabled>Select Sensor</option>
            <option v-for="sensor in sensorOptions" :key="sensor.sensor_id" :value="sensor">
              {{ sensor.label }}
            </option>
          </select>
        </div>

        <div class="alarm-field" v-if="sensorType !== 3">
          <small class="d-block text-muted">Register</small>
          <select class="form-control text-center" v-model="selectedRegister" @change="setAlarmRegister">
            <option :value="null" disabled>Select Register</option>
            <option v-for="register in registerOptions" :key="register.address" :value="register">
              {{ register.address }} - {{ register.description }}
            </option>
          </select>
        </div>

        <div class="alarm-field">
          <small class="d-block text-muted">Alarm Type</small>
          <div class="base-input-field">
            <select class="form-control text-center" v-model="localAlarmType">
              <option v-for="type in alarmTypes" :key="type.alarm_type_id" :value="type.alarm_type_id">
                {{ type.type_pretty_name }}
              </option>
            </select>
            <button type="button" class="help-btn help-icon-btn" ref="helpBtn">
              <i class="bi bi-question-circle-fill"></i>
            </button>
          </div>
        </div>

        <div v-if="alarm_type != 4 && alarm_type != 5 && alarm_type != 6" class="alarm-field">
          <small v-if="setPointError" class="d-block text-danger">{{ setPointError }}</small>
          <small class="d-block text-muted">Alarm Set Point</small>
          <input name="alarm-set-point" type="number" v-model="localAlarmValue" class="form-control text-center" />
        </div>

        <div v-if="alarm_type == 4 || alarm_type == 5" class="alarm-field">
          <small v-if="hysteresisError || setPointError" class="d-block text-danger">{{ hysteresisError || setPointError }}</small>
          <small class="d-block text-muted">Set Point</small>
          <input name="set-point" type="number" v-model="localAlarmValue" class="form-control text-center" />
          <small class="d-block text-muted">Clear Point</small>
          <input name="clear-point" type="number" v-model="localHysteresis" class="form-control text-center" />
        </div>

        <div class="alarm-field">
          <small class="d-block text-muted">Notifications</small>
          <Multiselect
              class="form-control w-auto"
              style="background-color: var(--color-bg-sec);border-color: var(--color-border); padding:0 0 0 6px;"
              mode="multiple"
              :options="toWhom"
              v-model="selectedNotifications"
              label="displayname"
              track-by="displayname"
              value-prop="displayname"
              placeholder="123456"
              :multiple="true"
              :can-clear="false"
              :close-on-select="false"
              :close-on-deselect="false"
              :clear-on-select="false"
              :clear-on-deselect="false"
              :searchable="false"
              :show-labels="false"
              :hide-selected="false"
              :object="true"
              :append-to-body="true"
              @close="setNotifications"
          >
            <template v-slot:placeholder>
              <span style="cursor: pointer; text-align:center; flex-grow: 1">
                0 users selected
              </span>
            </template>
            <template v-slot:nooptions>
              <span style="cursor: pointer; text-align:center; flex-grow: 1">
                Please select a sensor first
              </span>
            </template>
            <template v-slot:multiplelabel="{ values }">
              <span style="cursor: pointer; text-align:center; flex-grow: 1">
                {{ values.length }} users selected
              </span>
            </template>
            <template v-slot:option="{ option }">
              <div class="d-flex align-items-center option-item">
                <i v-if="selectedNotifications?.some(selected => selected.displayname === option?.displayname)"
                   class="bi bi-check custom-checkmark" />
                {{ option?.displayname }}
              </div>
            </template>
          </Multiselect>
        </div>
      </div>
    </div>

    <!-- Ellipsis Button for Menu -->
    <div class="alarm-actions">
      <button @click="toggleMenu" class="ellipsis-button">⋮</button>
      <div v-if="showMenu" class="dropdown-menu">
        <span class="ellipsis-menu-item" @click="removeAlarm">Delete</span>
      </div>
    </div>
  </div>
</template>

<script>
  import { useStore } from "vuex";
  import {computed, ref, watch, onMounted, onBeforeUnmount} from "vue";
  import { Popover } from 'bootstrap';
  import Multiselect from "@vueform/multiselect";

  export default {
    components: {
      Multiselect
  },
    props: {
      sensor_id: Number,
      alarm_type: String,
      alarm_value: Number,
      alarm_register: Number,
      alarm_action: String,
      hysteresis: Number,
      id: Number,
    },
    setup(props) {
      const store = useStore();
      const hysteresisError = ref('');
      // A sensor model can define more than one reading register at the same address, eg 299 being volume and raw reading
      const dedupeAddresses = (addresses) => {
        const byAddress = new Map();
        for (const reg of addresses || []) {
          if (!byAddress.has(reg.address) || reg.selected) {
            byAddress.set(reg.address, reg);
          }
        }
        return [...byAddress.values()];
      };

      const removeAlarm = function () {
        if (confirm('Are you sure you want to delete this alarm?')) {
          showMenu.value = false;
          store.commit('alarm/updateAlarmStatus', {
            id: props.id,
            status: 'deleted'
          });
          store.dispatch('gateway/findInfoOverview', {id: store.state.gateway.id});
        }
      }

      const setAlarmRegister = function() {
        store.commit('alarm/setRegister', {
          id: props.id,
          alarm_register: selectedRegister.value.address
        });
      }

      const validateHysteresis = (setPoint, clearPoint, alarmType) => {
        let sp = parseFloat(setPoint);
        let cp = parseFloat(clearPoint);
        let msg = '';
        if (alarmType == 4) {
          msg = sp < cp ? 'Clear Point must be less than Set Point' : '';
        } else {
          msg = sp > cp ? 'Set Point must be less than Clear Point' : '';
        }
        return msg;
      }

      const localAlarmValue = computed({
        get: () => store.getters["alarm/alarm"](props.id)?.alarm_set_point,
        set: (val) => {
          clearError();
          
          // Only validate hysteresis for alarm types that use it (4 and 5)
          if (localAlarmType.value == 4 || localAlarmType.value == 5) {
            hysteresisError.value = validateHysteresis(val, localHysteresis.value, localAlarmType.value);
            
            if (hysteresisError.value !== '') {
              setError(hysteresisError.value);
            }
          }
          
          // API now handles data transformations, so use the value directly
          store.commit('alarm/setSetPoint', {
            id: props.id,
            alarm_set_point: val,
          });
        }
      });

      const localHysteresis = computed({
        get: () => store.getters["alarm/alarm"](props.id)?.alarm_set_point_2,
        set: (val) => {
          clearError();
          
          // Only validate hysteresis for alarm types that use it (4 and 5)
          if (localAlarmType.value == 4 || localAlarmType.value == 5) {
            hysteresisError.value = validateHysteresis(localAlarmValue.value, val, localAlarmType.value);
            
            if (hysteresisError.value !== '') {
              setError(hysteresisError.value);
            }
          }
          
          // API now handles data transformations, so use the value directly
          store.commit('alarm/setHysteresis', {
            id: props.id,
            hysteresis: val,
          });
        }
      });
      
      const localAlarmType = computed({
        get: () => store.getters["alarm/alarm"](props.id)?.alarm_type_id,
        set: (val) => {
          clearError();
          if (val == 4 || val == 5) {
            hysteresisError.value = validateHysteresis(localAlarmValue.value, localHysteresis.value, val);
          }

          if (hysteresisError.value === '') {
            store.commit('alarm/setAlarmType', {
              id: props.id,
              alarm_type_id: val,
            });
        } else {
          setError(hysteresisError.value);
          localAlarmValue.value = null;
          localHysteresis.value = null;
        }
      }
    });

      const alarmTypeHelpImg = computed(() => {
        switch (String(localAlarmType.value)) {
          case '1': return '/help/under-setpoint.png'
          case '2': return '/help/over-setpoint.png'
          case '3': return '/help/abrupt-change.png'
          case '4': return '/help/Hysteresis-over.png'
          case '5': return '/help/Hysteresis-under.png'
          default: return ''
        }
      });

      const alarmTypeHelpHtml = computed(() =>
          alarmTypeHelpImg.value
              ? `<img src="${alarmTypeHelpImg.value}" alt="Help graphic" style="max-width:100%;height:auto;"/>`
              : ''
      );

      const helpBtn = ref(null);
      let popoverInstance = null;

      const updatePopover = () => {
        if (popoverInstance) {
          popoverInstance.dispose();
          popoverInstance = null;
        }
        if (helpBtn.value && alarmTypeHelpHtml.value) {
          popoverInstance = new Popover(helpBtn.value, {
            content: alarmTypeHelpHtml.value,
            html: true,
            sanitize: false,
            container: 'body',
            trigger: 'hover focus',
            placement: 'right'
          });
        }
      }

      watch(alarmTypeHelpHtml, updatePopover);
      onMounted(updatePopover);
      onBeforeUnmount(() => {
        if (popoverInstance) popoverInstance.dispose();
      });

      const selectedNotifications = ref(store.getters["alarm/selectedNotify"](props.id));

      const setNotifications = () => {
        // if the arrays are different, update the status
        store.commit('alarm/setSelectedNotifications', {
          id: props.id,
          selected_notify: selectedNotifications.value
        });
      };

      const setAlarmAction = (e) => {
        store.commit('alarm/setAlarmActionType', {
          id: props.id,
          alarm_action_type_id: e.target.value
        });
      };

      const calcSelected = (values) => values?.length || 0;

      const setClass = (status) => {
        if (status === 'existing') return;
        return status;
      }

      const setError = (error) => {
        store.commit('alarm/setAlarmError', {
          id: props.id,
          error: error
        });
      }

      const clearError = () => {
        hysteresisError.value = '';
        store.commit('alarm/clearAlarmError', {
          id: props.id,
        });
      }
      
      // errors the API sends back on save, eg a set point beyond what the tank holds. Cleared when the set point is edited
      const setPointError = computed(() => {
        let error = store.getters["alarm/alarm"](props.id)?.error;
        return typeof error === 'string' ? error : '';
      });

      const allGatewaySensors = computed(() => store.getters["MyView/getGateway"](store.state.gateway.id)?.nodes || []);
      
      // Use a ref to store the resolved math, so it persists even if sensorOptions flickers during reload
      const resolvedRegisterMath = ref(null);

      watch(() => [props.sensor_id, props.alarm_register, allGatewaySensors.value], () => {
        const sensors = allGatewaySensors.value;
        // If sensors are not loaded yet, do not clear the existing math (prevents flash of raw value)
        if (!sensors || sensors.length === 0) {
          return;
        }

        const sensor = sensors.find(s => s.id == props.sensor_id);
        if (!sensor) {
          return;
        }

        const register = dedupeAddresses(sensor?.addresses).find(r => r.address == props.alarm_register);
        
        // Only update if register is explicitly found. If not found (e.g. during loading), keep old value.
        if (register) {
          resolvedRegisterMath.value = register.formula || register.math || null;
        }
      }, { immediate: true });
      
      const registerExtraMath = computed(() => {
        return resolvedRegisterMath.value ||
               store.getters['alarm/registerExtraMath'] ||
               selectedRegister.value?.formula ||
               selectedRegister.value?.math ||
               null;
      });

      // Removed applyExtraMath and inverseExtraMath functions as API now handles data transformations
      const sensorOptions = computed(() => {
        const nodes = store.getters["MyView/getGateway"](store.state.gateway.id)?.nodes || [];
        const activeSensorIds = [...new Set(store.state.readingControl.controls.map(c => c.sensor_id))];
        return nodes.filter(n => activeSensorIds.includes(n.id));
      });
      
      const selectedSensor = ref(null);
      const selectedRegister = ref(null);

      watch(() => [props.sensor_id, props.alarm_register, sensorOptions.value], () => {
        if (sensorOptions.value) {
          selectedSensor.value = sensorOptions.value.find(sensor => sensor.id == props.sensor_id);
          if (selectedSensor.value) {
            selectedRegister.value = dedupeAddresses(selectedSensor.value?.addresses).find(register => register.address == props.alarm_register) ?? null;
          } else {
            selectedRegister.value = null;
          }
        }
      }, { immediate: true });

      const changeSelectedSensor = async () => {
        if (!selectedSensor.value) return;
        let data = store.getters["alarm/alarm"](props.id);
        let toWhom = await store.dispatch('alarm/fetchNotificationOptions', selectedSensor.value.id);
        store.commit('alarm/updateAlarm', {
          ...data,
          id: props.id,
          sensor_id: selectedSensor.value.id,
          sensor_type_id: selectedSensor.value.sensor_type_id,
          alarm_register: null,
          register_type_id: null,
          toWhom: toWhom
        });
      };

      const showMenu = ref(false);
      const toggleMenu = () => showMenu.value = !showMenu.value;
      

      return {
        removeAlarm,
        setAlarmRegister,
        alarmTypes: computed(() => store.state.alarm.alarm_type_options),
        setNotifications,
        selectedNotifications,
        setAlarmAction,
        alarmActionTypes: computed(() => store.state.alarm.alarm_action_type_options),
        calcSelected,
        setClass,
        toWhom: computed(() => store.getters["alarm/toWhom"](props.id)),
        sensorOptions,
        changeSelectedSensor,
        sensorId: computed(() => selectedSensor.value?.sensor_id),
        sensorType: computed(() => selectedSensor.value?.sensor_type_id),
        registerOptions: computed(() => {
          if (!selectedSensor.value) return [];
          const activeControls = store.state.readingControl.controls.filter(c => c.sensor_id == selectedSensor.value.id);
          const activeAddresses = activeControls.map(c => parseInt(c.sensor_register));
          return dedupeAddresses(selectedSensor.value.addresses).filter(r => activeAddresses.includes(parseInt(r.address)));
        }),
        selectedSensor,
        selectedRegister,
        status: computed(() => store.getters["alarm/setAlarmStatus"](props.id)),
        showMenu,
        toggleMenu,
        hysteresisError,
        setPointError,
        localAlarmValue,
        localHysteresis,
        localAlarmType,
        alarmTypeHelpHtml,
        registerExtraMath,
        helpBtn,
      };
    },
  };
</script>
<style scoped>
li {
  cursor: pointer;
}
div.new  {
  border: 3px solid #41b883;
}

div.updated {
  border: 3px solid orange;
}

div.deleted {
  text-decoration: line-through;
  color: var(--color-txt);
  opacity: 0.5;
  pointer-events: none;
}

.text-muted {
  color: var(--color-txt)!important;
}

.alarm-scrollable {
  overflow-x: auto;
  flex-grow: 1;
}

.alarm-content {
  display: flex;
  align-items: center;
}

.alarm-content > * {
  flex-shrink: 0;
  margin-left: 12px;
}

.alarm-actions {
  position: relative;
  flex-shrink: 0;
}

.ellipsis-button {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding-left: 8px;
  color: var(--color-txt);
}

.dropdown-menu {
  position: absolute;
  right: 0;
  top: 100%;
  background: var(--color-bg-sec);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 8px;
  z-index: 1000;
  display: block;
  min-width: 75px;
  white-space: nowrap;
}

small {
  margin: 0!important;
  padding: 0!important;
  font-size: 0.75em;
  color: var(--color-txt);
}

.alarm-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: background 0.5s ease, color 0.5s ease;
  overflow-y: visible;
  position: relative;
  white-space: nowrap;
}

.alarm-field {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: fit-content;
}

.alarm-field >select, .alarm-field >input {
  background: var(--color-bg-sec);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  color: var(--color-txt);
  width: 100%;
  max-width: 100%;
  white-space: nowrap;
}

.ellipsis-button {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--color-txt);
  transition: background 0.5s ease, color 0.5s ease;
}

.alarm-actions {
  position: relative;
}

.ellipsis-menu-item {
  cursor: pointer;
  padding: 4px;
  color: var(--color-txt);
}

.ellipsis-menu-item:hover {
  color: var(--color-sec);
}

.base-input-field input,
.base-input-field select,
.base-input-field textarea {
  background-color: var(--color-bg-sec);
}

</style>
<style src="@vueform/multiselect/themes/default.css"></style>
